/**
 * HR help text for loan and advance requests.
 *
 * An approved request is paid out and then recovered through payroll. Payroll
 * picks up repayment rows whose repayment date falls inside the salary month's
 * cut-off, so the repayment dates decide when money starts coming back.
 */

const lendRequest = {
  employment_id: "Employee requesting the loan or advance. Their approval workflow decides who receives the request.",
  lend_category_id: "Whether this is a loan or an advance. It decides which payroll deduction the repayments appear under.",
  lend_type_id: "The specific type being requested. Its rules decide the maximum amount, the required service, the gap since the last request, and how many repayment rows are generated.",
  amount: "Amount the employee is asking for. If the type has a formula limit, a larger amount is rejected.",
  approved_amount: "Amount actually approved. The approver can reduce it below what was requested, and only this figure is paid out and recovered.",
  currency: "Currency the amount is in.",
  effective_date: "Date the loan or advance takes effect. Repayment dates are generated forward from here, so a later date delays the first deduction.",
  repayment_date: "Date this instalment is deducted. Payroll deducts an instalment in the salary month whose cut-off covers this date.",
  payment: "Amount deducted in this instalment.",
  payment_method_id: "How the approved amount is paid out to the employee.",
  reason: "Why the employee needs this loan or advance. Approvers read this before approving.",
  description: "Any extra detail about the request.",
  extension_no: "Office extension where the employee can be reached about this request.",
  paid_amount: "How much has already been recovered from the employee so far.",
  remaining_amount: "How much is still to be recovered. Payroll keeps deducting until this reaches zero.",
  all_paid_amount: "Total already recovered across all of this employee's loans and advances.",
  all_approved_amount: "Total approved across all of this employee's loans and advances. Use it to judge how much they are already committed to.",
  created_at: "When the request was submitted.",
  is_active: "Only active requests move through the approval workflow.",
  last_remarks: "Remarks written by the last approver. Visible in the request history.",
  lend_type_name: "The loan or advance type on this request.",
  experience_letter: "Supporting document attached to this request, kept on file.",
};

const guarantor = {
  name: "Full name of the person guaranteeing this loan. Required when the loan type asks for a guarantor.",
  cnic: "Guarantor's identity number, kept so the guarantee can be enforced.",
  known_since: "How long the employee has known the guarantor. HR uses it to judge how genuine the guarantee is.",
  relationship_id: "How the guarantor is related to the employee.",
  guarantor_type: "Whether the guarantor is a colleague from inside the company or someone outside it.",
  extension: "Office extension for a guarantor who works in the company.",
  description: "Any note about this guarantor.",
  employment_id: "The guarantor's own employment record, when they work in the company.",
  experience_letter: "Supporting document for the guarantor, kept on file.",
  extension_no: "Office extension for a guarantor who works in the company.",
};

const lendCategory = {
  name: "Name of this category, such as Loan or Advance. It decides which payroll deduction the repayments of its types appear under.",
  description: "Internal note explaining when this category should be used.",
  is_active: "Only active categories can be selected on a new request.",
};

const repayment = {
  repayment_date: "Date this instalment is deducted. Payroll deducts it in the salary month whose cut-off covers this date, so moving it forward delays the deduction by a month.",
  payment: "Amount deducted in this instalment. The instalments together must cover the approved amount.",
  amount: "Amount for this instalment.",
  paid_amount: "How much has already been recovered.",
  remaining_amount: "How much is still to be recovered from the employee.",
  description: "Reason for adjusting this repayment. Kept for audit.",
  approved_amount: "Amount approved on the request these repayments recover. The instalments together must cover it.",
  all_paid_amount: "Total already recovered across all of this employee's loans and advances.",
  all_approved_amount: "Total approved across all of this employee's loans and advances.",
};

export const lendRequestTooltips = { lendRequest, guarantor, lendCategory, repayment };
export default lendRequestTooltips;
